import type { MaybeRefOrGetter } from 'vue';
import { useLocalStorageRef } from './useLocalStorage';

/**
 * Maps locale codes with region/country variants to their base language code.
 * Used to normalize specific locales (e.g., 'es-mx') to generic language codes (e.g., 'es').
 */
export const localeToBaseLanguageMap = Object.freeze({
  'es-mx': 'es',
  'fr-ca': 'fr',
  'pt-br': 'pt'
});

/**
 * Maps supported locales to human-readable language labels and flag emojis.
 * Used for displaying locale options in UI components like language selectors.
 */
export const localeToReadableLanguageMap = Object.freeze({
  en: { label: 'English (US)', flag: '🇺🇸' },
  'es-mx': { label: 'Español (MX)', flag: '🇲🇽' },
  'fr-ca': { label: 'Français (CA)', flag: '🇨🇦' },
  'pt-br': { label: 'Português (BR)', flag: '🇧🇷' }
});

/** Default locale to use when no preference is set or detected. */
const DEFAULT_LOCALE = 'en';

/**
 * Composable for managing user language/locale preferences with browser detection fallback.
 * Stores preferences per user in localStorage and provides utilities for locale normalization and display.
 *
 * @param userId - Optional user ID to scope localStorage to a specific user. Allows different users on the same device to have different locale preferences.
 * @returns Object containing locale state and utility functions for managing and retrieving locale information.
 */
export function useLocaleHelpers(userId?: MaybeRefOrGetter<string | undefined>) {
  const {
    ref: localeRef,
    set: setLocale,
    get: getLocale
  } = useLocalStorageRef('locale', undefined, userId !== undefined ? { userId } : undefined);

  /**
   * Converts a locale code to its base language code.
   * Handles region-specific locales by mapping them to their base language.
   *
   * @param locale - The locale code to normalize (e.g., 'es-mx', 'en-US')
   * @returns The base language code (e.g., 'es', 'en'), or the original locale if no mapping exists
   *
   * @example
   * getBaseLanguageCodeFromLocale('es-mx') // Returns 'es'
   * getBaseLanguageCodeFromLocale('en-US') // Returns 'en'
   */
  function getBaseLanguageCodeFromLocale(locale: string) {
    const key = locale.toLowerCase();
    return localeToBaseLanguageMap[key] ?? key;
  }

  /**
   * Retrieves the human-readable label for a locale.
   * Used for displaying language options to users in UI components.
   *
   * @param locale - The locale code to look up (e.g., 'es-mx', 'en')
   * @returns The readable language label (e.g., 'Español (MX)'), or the original locale if not found
   *
   * @example
   * getReadableLanguageFromLocale('es-mx') // Returns 'Español (MX)'
   */
  function getReadableLanguageFromLocale(locale: string) {
    const key = locale.toLowerCase();
    return localeToReadableLanguageMap[key]?.label ?? key;
  }

  /**
   * Persists a locale preference to localStorage.
   * If a userId was provided to the composable, the preference is scoped to that user.
   *
   * @param locale - The locale to store. Defaults to 'en' if not provided.
   *
   * @example
   * setLocaleToLocalStorage('es-mx')
   */
  function setLocaleToLocalStorage(locale: string = DEFAULT_LOCALE) {
    setLocale(locale);
  }

  /**
   * Retrieves the stored locale preference from localStorage.
   * Returns undefined if no preference has been set.
   *
   * @returns The stored locale, or undefined if not found
   *
   * @example
   * const locale = getLocaleFromLocalStorage() // Returns 'es-mx' or undefined
   */
  function getLocaleFromLocalStorage() {
    return getLocale();
  }

  /**
   * Determines the preferred locale for a user with intelligent fallback logic.
   *
   * Resolution order:
   * 1. Returns stored preference from localStorage (if available)
   * 2. Detects browser language and matches against supported locales (with bidirectional prefix matching)
   * 3. Falls back to 'en' if no match found
   * 4. Persists the determined locale for future visits
   *
   * The bidirectional matching handles both generic and specific language codes:
   * - 'en-US' (browser) matches 'en' (supported)
   * - 'es' (browser) matches 'es-mx' (supported)
   *
   * @returns The preferred locale, prioritizing user preference, then browser detection, then default 'en'
   *
   * @example
   * const locale = getPreferredLocale() // Returns 'es-mx', 'en', etc.
   */
  function getPreferredLocale() {
    const storedLocale = getLocale();
    const browserLang = navigator.language?.toLowerCase();

    if (!storedLocale && browserLang) {
      const supportedLocales = Object.keys(localeToReadableLanguageMap);
      const matchedLocale =
        supportedLocales.find(
          // Match supported locales bidirectionally:
          // - If browser language is more specific (e.g., "en-US" matches "en")
          // - If supported locale is more specific (e.g., "es-mx" matches "es")
          locale => browserLang.startsWith(locale) || locale.startsWith(browserLang)
        ) ?? DEFAULT_LOCALE;
      setLocale(matchedLocale);
      return matchedLocale;
    }

    return storedLocale ?? DEFAULT_LOCALE;
  }

  return {
    localeRef,
    getBaseLanguageCodeFromLocale,
    setLocaleToLocalStorage,
    getLocaleFromLocalStorage,
    getReadableLanguageFromLocale,
    getPreferredLocale
  };
}
